import React, { useState, useEffect } from 'react';
import { useAppStore } from '../store';
import { createClaim, getClaimUrl, getClaimMessage } from '../utils/claimForm';
import ClaimLinkModal from '../components/modals/ClaimLinkModal';

type EntityTab = 'production' | 'sample' | 'express_order' | 'express_sample';

const ClaimsLinkGenerator: React.FC = () => {
  const { productions, samples, express, expressSamples, clients, fetchProductions, fetchSamples, fetchExpress, fetchExpressSamples, fetchClients } = useAppStore();
  const [activeTab, setActiveTab] = useState<EntityTab>('production');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [linkModal, setLinkModal] = useState<{ token: string; entityName: string } | null>(null);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchProductions();
    fetchSamples();
    fetchExpress();
    fetchExpressSamples();
    fetchClients();
  }, [fetchProductions, fetchSamples, fetchExpress, fetchExpressSamples, fetchClients]);

  const getClientName = (clientId: string) => clients.find((c) => c.id === clientId)?.name || '';

  const productionList = productions
    .filter((p) => !p.archived)
    .map((p) => ({
      id: p.id,
      name: p.name,
      clientName: (p as any).client_name || getClientName(p.client_id),
      salesperson: (p as any).salesperson || '',
      clientId: p.client_id,
    }));

  const sampleList = samples
    .filter((s) => !s.archived)
    .map((s) => ({
      id: s.id,
      name: s.name,
      clientName: (s as any).client_name || getClientName(s.client_id),
      salesperson: (s as any).salesperson || '',
      clientId: s.client_id,
    }));

  const expressList = express.map((e: any) => ({
    id: e.id,
    name: e.name || `Pedido Express ${e.id?.slice(0, 8)}`,
    clientName: e.client_name || getClientName(e.client_id),
    salesperson: e.salesperson || '',
    clientId: e.client_id,
  }));

  const expressSampleList = expressSamples.map((s: any) => ({
    id: s.id,
    name: s.name || `Muestra Express ${s.id?.slice(0, 8)}`,
    clientName: s.client_name || getClientName(s.client_id),
    salesperson: s.salesperson || '',
    clientId: s.client_id,
  }));

  const lists: Record<EntityTab, typeof productionList> = {
    production: productionList,
    sample: sampleList,
    express_order: expressList,
    express_sample: expressSampleList,
  };

  const entityLabels: Record<EntityTab, string> = {
    production: 'Producción',
    sample: 'Muestra',
    express_order: 'Pedido Express',
    express_sample: 'Muestra Express',
  };

  const entityTypes: Record<EntityTab, 'production' | 'sample' | 'express_order' | 'express_sample'> = {
    production: 'production',
    sample: 'sample',
    express_order: 'express_order',
    express_sample: 'express_sample',
  };

  const currentList = lists[activeTab].filter(
    (item) =>
      !search ||
      item.name.toLowerCase().includes(search.toLowerCase()) ||
      item.clientName.toLowerCase().includes(search.toLowerCase())
  );

  const handleGenerate = async () => {
    if (!selectedId) return;
    const item = lists[activeTab].find((i) => i.id === selectedId);
    if (!item?.salesperson) {
      setError('La entidad no tiene vendedor asignado');
      return;
    }
    setGenerating(true);
    setError(null);
    try {
      const result = await createClaim({
        entityType: entityTypes[activeTab],
        entityId: item.id,
        entityName: item.name,
        clientName: item.clientName,
        salesperson: item.salesperson,
        clientId: item.clientId,
      });
      if (result) {
        setLinkModal({ token: result.token, entityName: item.name });
        setSelectedId(null);
      } else {
        setError('No se pudo generar el link');
      }
    } catch (e: any) {
      setError(e.message || 'Error al generar');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Generar formulario de reclamo</h1>
      <p className="text-gray-600 dark:text-gray-400 mb-6">
        Seleccioná una producción, muestra, pedido express o muestra express para generar un link único del formulario de reclamo que podés enviar al cliente.
      </p>

      <div className="flex gap-2 mb-4 overflow-x-auto">
        {(Object.keys(entityLabels) as EntityTab[]).map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => { setActiveTab(tab); setSelectedId(null); setSearch(''); }}
            className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap ${
              activeTab === tab
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            {entityLabels[tab]}
          </button>
        ))}
      </div>

      <div className="mb-4">
        <input
          type="text"
          placeholder="Buscar por nombre o cliente..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
        />
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 rounded-xl text-red-600 dark:text-red-400 text-sm">
          {error}
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="max-h-[400px] overflow-y-auto">
          {currentList.length === 0 ? (
            <div className="p-12 text-center text-gray-500 dark:text-gray-400">No hay entidades para mostrar</div>
          ) : (
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
              {currentList.map((item) => (
                <li
                  key={item.id}
                  onClick={() => setSelectedId(selectedId === item.id ? null : item.id)}
                  className={`flex items-center justify-between px-4 py-3 cursor-pointer transition-colors ${
                    selectedId === item.id ? 'bg-indigo-50 dark:bg-indigo-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                  }`}
                >
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">{item.name}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{item.clientName}</p>
                  </div>
                  {selectedId === item.id && (
                    <span className="text-indigo-600 dark:text-indigo-400 font-medium text-sm">Seleccionado</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={handleGenerate}
            disabled={!selectedId || generating}
            className="w-full py-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold transition-colors"
          >
            {generating ? 'Generando...' : 'Generar formulario de reclamo'}
          </button>
        </div>
      </div>

      <ClaimLinkModal
        isOpen={!!linkModal}
        onClose={() => setLinkModal(null)}
        token={linkModal?.token || ''}
        entityName={linkModal?.entityName || ''}
      />
    </div>
  );
};

export default ClaimsLinkGenerator;
